import { useApolloClient } from '@apollo/client/react';
import useUpdateCO2CalculatedValues from 'hooks/co2-calculator/useUpdateCO2CalculatedValues';
import { cloneDeep } from 'lodash';
import { useParams } from 'react-router-dom';
import Collapsible from 'react-collapsible';
import { useState, useCallback } from 'react';
import { SectionWrapper } from 'components/common/heap';
import { useIntl } from 'react-intl';
import {
  CO2CalculationsDocument,
  type CO2CalculationsQuery,
  type CO2CalculationsQueryVariables,
} from 'graphql/generated/graphql';
import requireValue from 'utils/requireValue';
import HeatingResult from './HeatingResult';
import HotWaterResult from './HotWaterResult';
import ElectricityResult from './ElectricityResult';
import type { ISaveCalculatedValueArgs, TCO2Calculations } from '../types';
import s from '../CO2CalculatorResult.module.scss';

interface IAllCalculationsProps {
  co2Calculations: TCO2Calculations;
}

const AllCalculations = ({ co2Calculations }: IAllCalculationsProps) => {
  const client = useApolloClient();
  const { formatMessage: t } = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const { id } = useParams<{ id?: string }>();
  // The calculators are rendered on the project routes (`/project/:id/...`) only.
  const projectId = requireValue(id, 'id');
  const updateCalculatedValue = useUpdateCO2CalculatedValues();

  const onUpdateCalculatedValueButtonClick = useCallback(
    async ({ callback, fieldName, fieldValue }: ISaveCalculatedValueArgs) => {
      const data = client.readQuery<CO2CalculationsQuery, CO2CalculationsQueryVariables>({
        query: CO2CalculationsDocument,
        variables: { projectId },
      });
      const newData = requireValue(cloneDeep(data), 'co2Calculations');
      requireValue(newData.co2Calculations, 'co2_emissions_before').co2_emissions_before.result[
        fieldName
      ].user_input = fieldValue;
      client.writeQuery<CO2CalculationsQuery, CO2CalculationsQueryVariables>({
        query: CO2CalculationsDocument,
        variables: { projectId },
        data: { ...newData },
      });
      callback();
      await updateCalculatedValue(projectId, { [fieldName]: fieldValue });
    },
    [client, projectId, updateCalculatedValue],
  );

  return (
    <Collapsible
      open={isOpen}
      handleTriggerClick={() => setIsOpen(!isOpen)}
      transitionTime={800}
      trigger={
        <div style={{ margin: '25px 0' }}>
          <span>{t({ id: 'renovation_house_wizard.should_show_all_calculations' })}</span>
          <div className={s.showAllCalculations}>
            {t({
              id: `renovation_house_wizard.${isOpen ? 'hide_calculations' : 'show_calculations'}`,
            })}
          </div>
        </div>
      }
    >
      <SectionWrapper>
        <SectionWrapper>
          <HeatingResult
            {...{ co2Calculations, onSaveButtonClick: onUpdateCalculatedValueButtonClick }}
          />
        </SectionWrapper>
        <SectionWrapper>
          <HotWaterResult
            {...{ co2Calculations, onSaveButtonClick: onUpdateCalculatedValueButtonClick }}
          />
        </SectionWrapper>
        <SectionWrapper>
          <ElectricityResult
            {...{ co2Calculations, onSaveButtonClick: onUpdateCalculatedValueButtonClick }}
          />
        </SectionWrapper>
      </SectionWrapper>
    </Collapsible>
  );
};

export default AllCalculations;
